<script lang="ts" setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { usePagination, uuid } from '@/composables'
import { useNotifyStore } from '@/stores'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type { WebhookEventsResponse, WebhookEvent, WebhookRetryResponse } from '@/types/fulfillment'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'WebhookHome' })

const notifyStore = useNotifyStore()

const loading = ref(true)
const events = ref<WebhookEvent[]>([])
const pagination = usePagination({ initialItemsPerPage: 50 })

// Filters
const statusFilter = ref<string>('failed')

const statusOptions = [
  { value: 'failed', title: 'Fehlgeschlagen' },
  { value: 'pending', title: 'Ausstehend' },
  { value: 'processing', title: 'In Verarbeitung' },
  { value: 'completed', title: 'Abgeschlossen' },
  { value: 'skipped', title: 'Übersprungen' }
]

// Retry confirmation dialog
const retryDialog = ref({
  show: false,
  event: null as WebhookEvent | null,
  loading: false
})

// Fetch webhook events
async function fetchEvents() {
  loading.value = true

  try {
    const { limit, offset } = pagination.buildQueryParams()
    const url = `/admin/webhook/failed?status=${statusFilter.value}&limit=${limit}&offset=${offset}`

    const res = await useAxios<null, WebhookEventsResponse>(
      'GET',
      url,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      events.value = res.data.events ?? []
      pagination.setTotal(res.data.count ?? 0)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'WebhookHome' })
  } finally {
    loading.value = false
  }
}

// Watch for filter and pagination changes
watch(() => pagination.page.value, () => {
  fetchEvents()
})

watch(statusFilter, () => {
  pagination.goToPage(1)
  fetchEvents()
})

// Pagination display helpers
const showingFrom = computed(() => {
  if (!events.value?.length) return 0
  return ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1
})

const showingTo = computed(() => {
  return Math.min(showingFrom.value + (events.value?.length ?? 0) - 1, pagination.total.value)
})

// Group errors by message to avoid repetitive display
const groupedErrors = computed(() => {
  const errorMap = new Map<string, number[]>()

  for (const event of events.value) {
    if (event.error) {
      const existingIds = errorMap.get(event.error) ?? []
      existingIds.push(event.id)
      errorMap.set(event.error, existingIds)
    }
  }

  return Array.from(errorMap.entries()).map(([message, ids]) => ({
    message,
    eventIds: ids,
    count: ids.length
  }))
})

// Check if event is a legacy event without payload
function isLegacyEvent(event: WebhookEvent): boolean {
  return event.error === 'legacy event without payload'
}

// Collapsible error section state
const showErrors = ref(true)

// Retry functions
function openRetryDialog(event: WebhookEvent) {
  retryDialog.value.event = event
  retryDialog.value.show = true
}

function closeRetryDialog() {
  retryDialog.value.show = false
  retryDialog.value.event = null
}

async function confirmRetry() {
  if (!retryDialog.value.event) return

  retryDialog.value.loading = true

  try {
    const eventId = retryDialog.value.event.id
    const res = await useAxios<null, WebhookRetryResponse>(
      'POST',
      `/admin/webhook/${eventId}/retry`,
      true
    )

    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: 'Erfolgreich',
        message: 'Webhook wurde zur Wiederholung eingereiht'
      })
      closeRetryDialog()
      fetchEvents()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'WebhookHome:retry' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    retryDialog.value.loading = false
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'completed': return 'status-completed'
    case 'failed': return 'status-failed'
    case 'processing': return 'status-processing'
    case 'pending': return 'status-pending'
    case 'skipped': return 'status-skipped'
    default: return ''
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'completed': return 'Abgeschlossen'
    case 'failed': return 'Fehlgeschlagen'
    case 'processing': return 'In Verarbeitung'
    case 'pending': return 'Ausstehend'
    case 'skipped': return 'Übersprungen'
    default: return status
  }
}

function getEventTypeColor(eventType: string): string {
  if (eventType.startsWith('payment_intent')) return 'type-payment'
  if (eventType.startsWith('charge')) return 'type-charge'
  if (eventType.startsWith('invoice')) return 'type-invoice'
  if (eventType.startsWith('customer')) return 'type-customer'
  if (eventType.startsWith('subscription')) return 'type-subscription'
  return 'type-default'
}

function copyToClipboard(text: string): void {
  navigator.clipboard.writeText(text)
  notifyStore.createNotify({
    id: uuid('v4'),
    alertType: 'success',
    title: 'Kopiert',
    message: 'Event ID wurde kopiert'
  })
}

onMounted(() => {
  fetchEvents()
})
</script>

<template>
  <BaseDashboardPage
    title="Webhook Monitor"
    subtitle="Stripe Webhook-Ereignisse überwachen und wiederholen"
    icon="mdi-webhook"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminFulfillment' }"
  >
    <template #actions>
      <button class="refresh-btn" @click="fetchEvents">
        <v-icon icon="mdi-refresh" size="18" />
        <span>Aktualisieren</span>
      </button>
    </template>

    <!-- Filters -->
    <div class="filters-container">
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        item-title="title"
        item-value="value"
        label="Status"
        variant="outlined"
        density="compact"
        hide-details
        class="filter-select glass-input"
      />
      <div class="filter-info">
        <v-icon icon="mdi-information-outline" size="16" />
        <span>{{ pagination.total.value }} Events mit Status "{{ getStatusLabel(statusFilter) }}"</span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular color="cyan" indeterminate size="40" width="3" />
      <span class="loading-text">Lade Webhook-Ereignisse...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="events.length === 0" class="empty-state">
      <div class="empty-icon">
        <v-icon icon="mdi-check-circle" size="48" />
      </div>
      <span class="empty-title">Keine Events gefunden</span>
      <span class="empty-subtitle">
        Keine Webhook-Ereignisse mit Status "{{ getStatusLabel(statusFilter) }}"
      </span>
    </div>

    <!-- Events List -->
    <template v-else>
      <div class="events-list">
        <div
          v-for="event in events"
          :key="event.id"
          class="event-card"
          :class="{
            'card-failed': event.status === 'failed',
            'card-legacy': isLegacyEvent(event)
          }"
        >
          <!-- Card Header -->
          <div class="card-header">
            <div class="card-id-section">
              <span class="event-db-id">#{{ event.id }}</span>
              <span v-if="isLegacyEvent(event)" class="legacy-badge" title="Kein Payload gespeichert">
                <v-icon icon="mdi-archive-off" size="14" />
                Legacy
              </span>
            </div>
            <div class="card-meta">
              <span class="meta-date">
                <v-icon icon="mdi-clock-outline" size="14" />
                {{ formatDate(event.received_at) }}
              </span>
              <span v-if="event.attempts > 0" class="meta-attempts" :class="{ 'high-attempts': event.attempts >= 3 }">
                <v-icon icon="mdi-repeat" size="14" />
                {{ event.attempts }} {{ event.attempts === 1 ? 'Versuch' : 'Versuche' }}
              </span>
            </div>
          </div>

          <!-- Card Body -->
          <div class="card-body">
            <div class="event-type-section">
              <span class="event-type-badge" :class="getEventTypeColor(event.event_type)">
                {{ event.event_type }}
              </span>
              <span class="status-indicator" :class="getStatusClass(event.status)">
                <v-icon
                  :icon="event.status === 'failed' ? 'mdi-alert-circle' : event.status === 'completed' ? 'mdi-check-circle' : 'mdi-clock'"
                  size="14"
                />
                {{ getStatusLabel(event.status) }}
              </span>
            </div>
            <div class="stripe-id-section">
              <span class="stripe-label">Stripe Event:</span>
              <code class="stripe-event-id" :title="event.event_id">{{ event.event_id }}</code>
              <button class="copy-btn" @click="copyToClipboard(event.event_id)" title="Event ID kopieren">
                <v-icon icon="mdi-content-copy" size="14" />
              </button>
            </div>
          </div>

          <!-- Card Footer / Actions -->
          <div class="card-actions">
            <button
              v-if="event.status === 'failed' && !isLegacyEvent(event)"
              class="action-btn retry-btn"
              @click="openRetryDialog(event)"
            >
              <v-icon icon="mdi-refresh" size="18" />
              <span>Wiederholen</span>
            </button>
            <span
              v-else-if="isLegacyEvent(event)"
              class="no-retry-hint"
            >
              <v-icon icon="mdi-cancel" size="16" />
              <span>Kein Payload</span>
            </span>
            <span v-else class="action-placeholder"></span>
          </div>
        </div>
      </div>

      <!-- Grouped Error Display -->
      <div v-if="groupedErrors.length > 0" class="errors-section">
        <button class="errors-toggle" @click="showErrors = !showErrors">
          <v-icon :icon="showErrors ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="18" />
          <v-icon icon="mdi-alert-circle" size="16" class="error-icon" />
          <span class="errors-title">
            {{ groupedErrors.length }} {{ groupedErrors.length === 1 ? 'Fehlertyp' : 'Fehlertypen' }}
          </span>
          <span class="errors-count">
            ({{ events.filter(e => e.error).length }} Events betroffen)
          </span>
        </button>

        <div v-show="showErrors" class="errors-list">
          <div
            v-for="(errorGroup, index) in groupedErrors"
            :key="`error-group-${index}`"
            class="error-group"
          >
            <div class="error-group-header">
              <v-icon icon="mdi-alert-circle" size="14" />
              <span class="error-message">{{ errorGroup.message }}</span>
            </div>
            <div class="error-group-ids">
              <span class="ids-label">Betroffene Events ({{ errorGroup.count }}):</span>
              <span class="ids-list">
                {{ errorGroup.eventIds.slice(0, 10).map(id => `#${id}`).join(', ') }}
                <template v-if="errorGroup.eventIds.length > 10">
                  ... und {{ errorGroup.eventIds.length - 10 }} weitere
                </template>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <span class="pagination-info">
          Zeige {{ showingFrom }}-{{ showingTo }} von {{ pagination.total.value }}
        </span>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="!pagination.hasPrevPage.value"
            @click="pagination.prevPage()"
          >
            <v-icon icon="mdi-chevron-left" size="18" />
          </button>
          <span class="page-indicator">
            Seite {{ pagination.page.value }} von {{ pagination.totalPages.value }}
          </span>
          <button
            class="page-btn"
            :disabled="!pagination.hasNextPage.value"
            @click="pagination.nextPage()"
          >
            <v-icon icon="mdi-chevron-right" size="18" />
          </button>
        </div>
      </div>
    </template>

    <!-- Retry Confirmation Dialog -->
    <v-dialog v-model="retryDialog.show" max-width="450">
      <div class="retry-dialog">
        <div class="dialog-header">
          <v-icon icon="mdi-refresh" size="24" />
          <h3>Webhook erneut verarbeiten?</h3>
        </div>
        <div class="dialog-content">
          <p>Das Webhook-Event wird erneut zur Verarbeitung eingereiht.</p>
          <div v-if="retryDialog.event" class="event-details">
            <div class="detail-row">
              <span class="detail-label">Event ID:</span>
              <span class="detail-value">{{ retryDialog.event.event_id }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Typ:</span>
              <span class="detail-value">{{ retryDialog.event.event_type }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Bisherige Versuche:</span>
              <span class="detail-value">{{ retryDialog.event.attempts }}</span>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button
            class="dialog-btn cancel-btn"
            :disabled="retryDialog.loading"
            @click="closeRetryDialog"
          >
            Abbrechen
          </button>
          <button
            class="dialog-btn confirm-btn"
            :disabled="retryDialog.loading"
            @click="confirmRetry"
          >
            <v-progress-circular
              v-if="retryDialog.loading"
              indeterminate
              size="16"
              width="2"
            />
            <template v-else>
              <v-icon icon="mdi-refresh" size="16" />
              <span>Wiederholen</span>
            </template>
          </button>
        </div>
      </div>
    </v-dialog>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: 10px;
  color: #00b0ff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.35);
  }
}

/* Filters */
.filters-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  min-width: 200px;
}

.filter-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);

  .v-icon {
    color: rgba(200, 218, 255, 0.4);
  }
}

/* Loading & Empty States */
.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 300px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 20px;

  .v-icon {
    color: #4caf50;
  }
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.empty-subtitle {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Events List - Card Layout */
.events-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-card {
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(15, 23, 48, 0.7);
    border-color: rgba(120, 163, 255, 0.18);
  }

  &.card-failed {
    border-left: 4px solid #ef5350;
  }

  &.card-legacy {
    opacity: 0.65;

    &:hover {
      opacity: 0.8;
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.06);
}

.card-id-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.event-db-id {
  font-size: 0.9rem;
  font-weight: 700;
  color: rgba(200, 218, 255, 0.8);
}

.legacy-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(158, 158, 158, 0.15);
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 16px;
}

.meta-date,
.meta-attempts {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);

  .v-icon {
    opacity: 0.7;
  }
}

.meta-attempts {
  padding: 4px 10px;
  background: rgba(255, 193, 7, 0.08);
  border-radius: 6px;
  color: rgba(255, 193, 7, 0.8);

  &.high-attempts {
    background: rgba(239, 83, 80, 0.1);
    color: #ef5350;
  }
}

.card-body {
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.event-type-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.event-type-badge {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'SF Mono', Monaco, monospace;

  &.type-payment {
    background: rgba(156, 39, 176, 0.12);
    border: 1px solid rgba(156, 39, 176, 0.25);
    color: #ce93d8;
  }

  &.type-charge {
    background: rgba(0, 176, 255, 0.1);
    border: 1px solid rgba(0, 176, 255, 0.2);
    color: #4fc3f7;
  }

  &.type-invoice {
    background: rgba(76, 175, 80, 0.1);
    border: 1px solid rgba(76, 175, 80, 0.2);
    color: #81c784;
  }

  &.type-customer {
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.2);
    color: #ffb74d;
  }

  &.type-subscription {
    background: rgba(233, 30, 99, 0.1);
    border: 1px solid rgba(233, 30, 99, 0.2);
    color: #f48fb1;
  }

  &.type-default {
    background: rgba(120, 163, 255, 0.1);
    border: 1px solid rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.9);
  }
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;

  &.status-completed {
    background: rgba(76, 175, 80, 0.12);
    color: #4caf50;
  }

  &.status-failed {
    background: rgba(239, 83, 80, 0.12);
    color: #ef5350;
  }

  &.status-processing {
    background: rgba(0, 176, 255, 0.12);
    color: #00b0ff;
  }

  &.status-pending {
    background: rgba(255, 193, 7, 0.12);
    color: #ffc107;
  }

  &.status-skipped {
    background: rgba(158, 158, 158, 0.12);
    color: #9e9e9e;
  }
}

.stripe-id-section {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.stripe-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.stripe-event-id {
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 0.82rem;
  color: rgba(200, 218, 255, 0.75);
  background: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.copy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(120, 163, 255, 0.2);
    color: rgba(255, 255, 255, 0.9);
  }
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  padding: 12px 18px;
  background: rgba(255, 255, 255, 0.015);
  border-top: 1px solid rgba(120, 163, 255, 0.06);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn {
  background: rgba(0, 176, 255, 0.12);
  border: 1px solid rgba(0, 176, 255, 0.25);
  color: #00b0ff;

  &:hover {
    background: rgba(0, 176, 255, 0.2);
    border-color: rgba(0, 176, 255, 0.4);
    transform: translateY(-1px);
  }
}

.no-retry-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 0.8rem;
  color: rgba(158, 158, 158, 0.6);
}

.action-placeholder {
  height: 42px;
}

/* Grouped Error Display */
.errors-section {
  margin-top: 16px;
  background: rgba(239, 83, 80, 0.03);
  border: 1px solid rgba(239, 83, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
}

.errors-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(239, 83, 80, 0.05);
  }

  .v-icon:first-child {
    color: rgba(200, 218, 255, 0.5);
  }

  .error-icon {
    color: #ef5350;
  }
}

.errors-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
}

.errors-count {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.errors-list {
  border-top: 1px solid rgba(239, 83, 80, 0.1);
}

.error-group {
  padding: 14px 20px;
  border-bottom: 1px solid rgba(239, 83, 80, 0.08);

  &:last-child {
    border-bottom: none;
  }
}

.error-group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;

  .v-icon {
    color: #ef5350;
    flex-shrink: 0;
  }
}

.error-message {
  font-size: 0.85rem;
  color: #ef5350;
  word-break: break-word;
}

.error-group-ids {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 24px;
}

.ids-label {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.4);
}

.ids-list {
  font-size: 0.75rem;
  font-family: monospace;
  color: rgba(200, 218, 255, 0.6);
}

/* Pagination */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
}

.pagination-info {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.7);
}

/* Retry Dialog */
.retry-dialog {
  background: #0f1730;
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: rgba(0, 176, 255, 0.08);
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);

  .v-icon {
    color: #00b0ff;
  }

  h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
  }
}

.dialog-content {
  padding: 20px 24px;

  p {
    margin: 0 0 16px 0;
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.7);
    line-height: 1.5;
  }
}

.event-details {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  padding: 14px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
}

.detail-label {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  min-width: 120px;
}

.detail-value {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-all;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: rgba(15, 23, 48, 0.3);
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.dialog-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.cancel-btn {
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  color: rgba(200, 218, 255, 0.7);

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
  }
}

.confirm-btn {
  background: rgba(0, 176, 255, 0.15);
  border: 1px solid rgba(0, 176, 255, 0.3);
  color: #00b0ff;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.25);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .page-title,
  .empty-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .page-subtitle,
  .empty-subtitle,
  .loading-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .events-table,
  .loading-container,
  .empty-state,
  .filters-container {
    background: rgba(255, 255, 255, 0.7);
  }

  .filter-select {
    background: rgba(255, 255, 255, 0.9);
    color: rgba(15, 23, 42, 0.9);

    option {
      background: white;
      color: rgba(15, 23, 42, 0.9);
    }
  }

  .retry-dialog {
    background: white;

    .dialog-header {
      background: rgba(0, 176, 255, 0.05);
    }

    h3 {
      color: rgba(15, 23, 42, 0.95);
    }

    .dialog-content p {
      color: rgba(15, 23, 42, 0.7);
    }

    .event-details {
      background: rgba(240, 245, 255, 0.8);
    }

    .detail-value {
      color: rgba(15, 23, 42, 0.9);
    }
  }

  .errors-section {
    background: rgba(239, 83, 80, 0.05);
  }

  .errors-title {
    color: rgba(15, 23, 42, 0.85);
  }

  .errors-count,
  .ids-label {
    color: rgba(15, 23, 42, 0.5);
  }

  .ids-list {
    color: rgba(15, 23, 42, 0.7);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .webhook-home {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .back-link,
  .refresh-btn {
    width: 100%;
    justify-content: center;
  }

  .filters-container {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-select {
    width: 100%;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .card-meta {
    flex-wrap: wrap;
    gap: 8px;
  }

  .event-type-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .stripe-id-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .stripe-event-id {
    width: 100%;
    font-size: 0.75rem;
  }

  .card-actions {
    justify-content: stretch;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }

  .pagination-container {
    flex-direction: column;
    gap: 14px;
  }
}
</style>

<!-- Unscoped styles for Vuetify components - SSR compatibility -->
<style lang="scss">
/* Glass Input Styling for Vuetify components */
.glass-input .v-field {
  background: rgba(255, 255, 255, 0.04) !important;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.glass-input .v-field:hover {
  background: rgba(255, 255, 255, 0.06) !important;
}

.glass-input .v-field--focused {
  background: rgba(255, 255, 255, 0.08) !important;
}

.glass-input .v-field__overlay {
  background: transparent !important;
}

.glass-input .v-field__outline {
  --v-field-border-opacity: 0.15;
  color: rgba(120, 163, 255, 1);
}

.glass-input .v-field__outline__start,
.glass-input .v-field__outline__end {
  border-color: rgba(120, 163, 255, var(--v-field-border-opacity));
}

.glass-input .v-field__input,
.glass-input .v-select__selection-text {
  color: rgba(255, 255, 255, 0.95);
}

.glass-input .v-label {
  color: rgba(200, 218, 255, 0.6);
}
</style>
